import * as THREE from 'three';
import {PreviewPosition} from 'components/customer/Preview3D/types';

/**
 * Helper to collect measurement groups based on a filter function.
 * @param {THREE.Group} cabinetMeasurements - The cabinet measurements group
 * @param {THREE.Group} emptyspaceMeasurements - The empty space measurements group
 * @param {Function} filter - Filter function to determine which objects to include
 * @return {THREE.Group[]} Array of filtered measurement groups
 */
function collectMeasurementGroups(
    cabinetMeasurements: THREE.Group,
    emptyspaceMeasurements: THREE.Group,
    filter: (object: THREE.Group<THREE.Object3DEventMap>) => boolean
): THREE.Group[] {
    const measurementGroups: THREE.Group[] = [];

    // Process cabinet measurements
    if (cabinetMeasurements) {
        cabinetMeasurements.traverse((object: THREE.Object3D) => {
            if (object instanceof THREE.Group) {
                // Type guard: after instanceof check, object is now THREE.Group
                const group = object as THREE.Group<THREE.Object3DEventMap>;
                if (filter(group)) {
                    measurementGroups.push(group);
                }
            }
        });
    }

    if (emptyspaceMeasurements) {
        emptyspaceMeasurements.traverse((object: THREE.Object3D) => {
            if (object instanceof THREE.Group) {
                // Type guard: after instanceof check, object is now THREE.Group
                const group = object as THREE.Group<THREE.Object3DEventMap>;
                if (filter(group)) {
                    measurementGroups.push(group);
                }
            }
        });
    }

    return measurementGroups;
}

/**
 * Helper to compute the center of a group of objects.
 * @param {THREE.Object3D[]} objects - Array of 3D objects
 * @return {THREE.Vector3} The center position vector
 */
function computeCenter(objects: THREE.Object3D[]): THREE.Vector3 {
    const center = new THREE.Vector3();
    if (objects.length > 0) {
        for (const obj of objects) {
            center.add(obj.position);
        }
        center.divideScalar(objects.length);
    }
    return center;
}

/**
 * Helper to check bounding box overlap between two objects.
 * @param {THREE.Object3D} a - First object
 * @param {THREE.Object3D} b - Second object
 * @return {boolean} True if objects overlap, false otherwise
 */
function hasOverlap(a: THREE.Object3D, b: THREE.Object3D): boolean {
    const aBox = new THREE.Box3().setFromObject(a);
    const bBox = new THREE.Box3().setFromObject(b);

    return aBox.intersectsBox(bBox);
}

/**
 * Helper to check Z-axis (front/back) overlap between two objects.
 * @param {THREE.Object3D} a - First object
 * @param {THREE.Object3D} b - Second object
 * @param {number} [zThreshold=0.1] - Minimum Z overlap to consider as overlap
 * @return {boolean} Boolean indicating if there's significant Z-axis overlap
 */
function hasFrontZOverlap(
    a: THREE.Object3D,
    b: THREE.Object3D,
    zThreshold = 0.1
): boolean {
    const aBox = new THREE.Box3().setFromObject(a);
    const bBox = new THREE.Box3().setFromObject(b);

    // Calculate Z-axis overlap
    const zOverlap =
        Math.min(aBox.max.z, bBox.max.z) - Math.max(aBox.min.z, bBox.min.z);

    // Return true if there's significant Z overlap
    return zOverlap > zThreshold;
}

/**
 * Helper to check for overlaps among siblings of a group.
 * @param {THREE.Group} group - The group to check for sibling overlaps
 * @return {boolean} True if any siblings overlap, false otherwise
 */
function checkSiblingsOverlap(group: THREE.Group): boolean {
    if (!group || !group.children) return false;

    const children = group.children;
    for (let i = 0; i < children.length; i++) {
        for (let j = i + 1; j < children.length; j++) {
            // eslint-disable-next-line security/detect-object-injection -- Array indices are safe, not user-controlled object keys
            if (hasOverlap(children[i], children[j])) {
                return true;
            }
        }
    }
    return false;
}

/**
 * Helper to check for overlaps in emptyspace measurements.
 * @param {THREE.Group} emptyspaceMeasurements - The empty space measurements group
 * @return {boolean} True if any empty space objects overlap, false otherwise
 */
function checkEmptySpaceOverlap(emptyspaceMeasurements: THREE.Group): boolean {
    if (!emptyspaceMeasurements) return false;

    const emptySpaceObjects: THREE.Object3D[] = [];
    emptyspaceMeasurements.traverse((object: THREE.Object3D) => {
        if (object instanceof THREE.Group && object.children.length > 0) {
            emptySpaceObjects.push(object);
        }
    });

    for (let i = 0; i < emptySpaceObjects.length; i++) {
        for (let j = i + 1; j < emptySpaceObjects.length; j++) {
            // eslint-disable-next-line security/detect-object-injection -- Array indices are safe, not user-controlled object keys
            if (hasOverlap(emptySpaceObjects[i], emptySpaceObjects[j])) {
                return true;
            }
        }
    }
    return false;
}

/**
 * Helper to check for front Z overlaps specifically in emptyspace measurements.
 * @param {THREE.Group} emptyspaceMeasurements - The empty space measurements group
 * @param {number} [zThreshold=0.1] - Minimum Z overlap to consider as overlap
 * @return {boolean} True if any empty space objects have front Z overlap, false otherwise
 */
function checkEmptySpaceFrontZOverlap(
    emptyspaceMeasurements: THREE.Group,
    zThreshold = 0.1
): boolean {
    if (!emptyspaceMeasurements) return false;

    const emptySpaceObjects: THREE.Object3D[] = [];
    emptyspaceMeasurements.traverse((object) => {
        if (object instanceof THREE.Group && object.children.length > 0) {
            emptySpaceObjects.push(object);
        }
    });

    for (let i = 0; i < emptySpaceObjects.length; i++) {
        for (let j = i + 1; j < emptySpaceObjects.length; j++) {
            if (
                hasFrontZOverlap(
                    // eslint-disable-next-line security/detect-object-injection -- Array indices are safe, not user-controlled object keys
                    emptySpaceObjects[i],
                    // eslint-disable-next-line security/detect-object-injection -- Array indices are safe, not user-controlled object keys
                    emptySpaceObjects[j],
                    zThreshold
                )
            ) {
                return true;
            }
        }
    }
    return false;
}

/**
 * Helper to apply axis offset based on view and wall name.
 * @param {THREE.Object3D} object - The object to apply offset to
 * @param {number} offset - The offset amount
 * @param {string} name - The wall name
 * @param {string} previewPosition - The current preview position
 * @param {boolean} [isLShaped=false] - Whether the room is L-shaped
 * @param {boolean} [hasOverlap=false] - Whether there is overlap detected
 * @return {void}
 */
function getAxisOffset(
    object: THREE.Object3D,
    offset: number,
    name: string,
    previewPosition: PreviewPosition,
    isLShaped = false,
    hasOverlap = false
): void {
    // Wall name logic
    const isLeftWall =
        name === 'leftWall' ||
        (isLShaped && name === 'leftWidthWall') ||
        (isLShaped && name === ' rightDepthWall');
    const isRightWall =
        name === 'rightWall' ||
        (isLShaped && name === 'rightWidthWall') ||
        (isLShaped && name === 'leftDepthWall');

    const isBackWall = name === 'backWall';

    let effectiveOffset = offset;
    if (
        previewPosition === 'RIGHT_LEVELED' ||
        previewPosition === 'LEFT_LEVELED'
    ) {
        effectiveOffset = -offset;
    }

    if (hasOverlap) {
        if (
            previewPosition === 'TOP' ||
            previewPosition === 'TOP_WITHOUT_EDITING'
        ) {
            if (isLeftWall) {
                object.position.z -= effectiveOffset;
            } else if (isRightWall) {
                object.position.z += effectiveOffset;
            } else if (!isLShaped && isBackWall) {
                object.position.z += effectiveOffset;
            } else {
                object.position.z -= effectiveOffset;
            }
        } else {
            object.position.z += effectiveOffset;
        }
    } else {
        if (isLeftWall) {
            // object.position.z = -offset;
        } else {
            // object.position.z = offset;
        }
    }
}

/**
 * Generalized overlap prevention for measurement groups.
 * @param {string} name - Wall or group name
 * @param {THREE.Group} cabinetMeasurements - Cabinet measurement group
 * @param {THREE.Group} emptyspaceMeasurements - Emptyspace measurement group
 * @param {number} cabinetOffset - Offset for cabinet measurements
 * @param {string} previewPosition - Current preview position
 * @param {boolean} [isLShaped=false] - Whether to use L-shaped logic
 * @return {void}
 */
function preventSpriteOverlapGeneric(
    name: string,
    cabinetMeasurements: THREE.Group,
    emptyspaceMeasurements: THREE.Group,
    cabinetOffset: number,
    previewPosition: PreviewPosition,
    isLShaped = false
): void {
    const maxIterations = 1; // Safety limit to prevent infinite loops
    let iteration = 0;
    let hasAnyOverlap = true;

    // Continue while overlaps exist and haven't reached safety limit
    while (hasAnyOverlap && iteration < maxIterations) {
        // If no overlaps detected, break the loop
        if (!hasAnyOverlap) {
            break;
        }

        // Check for overlaps among siblings and emptyspace measurements
        const hasCabinetSiblingsOverlap =
            checkSiblingsOverlap(cabinetMeasurements);
        const hasEmptySpaceOverlap = checkEmptySpaceOverlap(
            emptyspaceMeasurements
        );

        // Check for front Z-axis specific overlaps
        const hasEmptySpaceFrontZOverlap = checkEmptySpaceFrontZOverlap(
            emptyspaceMeasurements
        );

        // Update the overall overlap status
        hasAnyOverlap =
            hasCabinetSiblingsOverlap ||
            hasEmptySpaceOverlap ||
            hasEmptySpaceFrontZOverlap;

        const baseOffset = cabinetOffset;

        // Filter for measurement groups
        const filter = isLShaped
            ? (object: THREE.Group<THREE.Object3DEventMap>) =>
                  object.children.some((child) => child.type === 'ArrowHelper')
            : (object: THREE.Group<THREE.Object3DEventMap>) =>
                  object.children.some(
                      (child) => child instanceof THREE.Sprite
                  ) &&
                  object.children.some((child) => child.type === 'ArrowHelper');

        const measurementGroups = collectMeasurementGroups(
            cabinetMeasurements,
            emptyspaceMeasurements,
            filter
        );
        computeCenter(measurementGroups); // Center is not used, but kept for possible future use

        const measurementList = measurementGroups.filter(
            (group) => group.name === 'multipleMeasurementsGroup'
        );

        const allMeasurements = measurementList
            .flatMap((group) => {
                const deepRootChildren = group.parent.parent.children
                    .flatMap((x) => x.children)
                    .filter((x) => x.name === 'multipleMeasurementsGroup')
                    .flatMap((x) => x.children);

                return deepRootChildren;
            })
            .filter(
                (item, index, array) =>
                    array.findIndex((otherItem) => otherItem.id === item.id) ===
                    index
            );

        let offset = baseOffset;

        const tidyUpMeasurements = (
            currentItem: THREE.Object3D<THREE.Object3DEventMap>,
            group: THREE.Group<THREE.Object3DEventMap>,
            increaseOffset = true
        ) => {
            if (hasOverlap(currentItem, group)) {
                if (currentItem instanceof THREE.Sprite) {
                    const samePosition = allMeasurements
                        .filter((ch) => {
                            const regularConditions = ch.id !== currentItem.id;
                            const positionConditions = ch.userData
                                .text as string;

                            return positionConditions && regularConditions;
                        })
                        .some((child) => hasOverlap(child, currentItem));

                    if (increaseOffset) {
                        if (samePosition) {
                            offset += cabinetOffset;
                        } else {
                            offset = cabinetOffset;
                        }
                    }
                }
                getAxisOffset(
                    currentItem,
                    offset,
                    name,
                    previewPosition,
                    isLShaped,
                    increaseOffset
                );
            }
        };

        measurementList
            .filter((group) =>
                group.children.some((item) => hasOverlap(item, group))
            )
            .forEach((group) => {
                group.children.forEach((item) => {
                    tidyUpMeasurements(item, group, true);
                });
            });

        iteration++;
    }
}

/**
 * Prevents overlap of all measurement groups (group containing both sprite and arrows) based on view direction.
 * @param {string} name - The measurement group name
 * @param {THREE.Group} cabinetMeasurements - Cabinet measurement group
 * @param {THREE.Group} emptyspaceMeasurements - Emptyspace measurement group
 * @param {number} cabinetOffset - Offset for cabinet measurements
 * @param {string} previewPosition - Current preview position
 * @return {void}
 */
export function preventSpriteOverlapByView(
    name: string,
    cabinetMeasurements: THREE.Group,
    emptyspaceMeasurements: THREE.Group,
    cabinetOffset: number,
    previewPosition: PreviewPosition
): void {
    preventSpriteOverlapGeneric(
        name,
        cabinetMeasurements,
        emptyspaceMeasurements,
        cabinetOffset,
        previewPosition,
        false
    );
}

/**
 * Prevents overlap of all measurement groups (group containing both sprite and arrows) based on view direction (L-shaped version).
 * @param {string} name - The measurement group name
 * @param {THREE.Group} cabinetMeasurements - Cabinet measurement group
 * @param {THREE.Group} emptyspaceMeasurements - Emptyspace measurement group
 * @param {number} cabinetOffset - Offset for cabinet measurements
 * @param {string} previewPosition - Current preview position
 * @return {void}
 */
export function preventLShapedSpriteOverlapByView(
    name: string,
    cabinetMeasurements: THREE.Group,
    emptyspaceMeasurements: THREE.Group,
    cabinetOffset: number,
    previewPosition: PreviewPosition
): void {
    preventSpriteOverlapGeneric(
        name,
        cabinetMeasurements,
        emptyspaceMeasurements,
        cabinetOffset,
        previewPosition,
        true
    );
}
